import type {Assignment} from '~/src/models/applicant'
import {BaseApi} from './BaseApi'
import {reportCatchError} from '../utils/errorReporter'

/**
 * Ronos (Staffology) sync status for an assignment.
 * Mirrors the backend RonosSync embedded document.
 */
export type RonosSyncStatus = 'Pending' | 'Synced' | 'Failed' | 'NotApplicable'

export interface RonosSyncInfo {
  status: RonosSyncStatus
  employeeCreated: boolean
  error?: string
  lastSyncAt?: Date | string
}

/**
 * Result of a manual Ronos sync trigger.
 * POST /api/assignment/sync-ronos/by-assignment/:assignmentId
 */
export interface RonosSyncResult {
  success: boolean
  error?: string
}

/**
 * Assignment Paging Parameters for search/filter
 */
export interface AssignmentPagingParameters {
  pageNumber: number
  pageSize: number
  agencyId?: string
  companyId?: string
  employeeId?: string
  jobOrderId?: string
  serviceOrderId?: string
  siteId?: string
  departmentId?: string
  positionId?: string
  assignmentId?: string
  /** Free-text search across candidate first/last name + last-4 SSN (server-side). */
  search?: string
  /**
   * Server-side status filter (exact match on the persisted BE status).
   * Note the capital "A" in `InActive` — the BE `@IsEnum(AssignmentStatus)` rejects `Inactive`.
   */
  status?: 'Active' | 'InActive'
}

/**
 * Assignment Status Enum
 * 0 = Pending, 1 = Active, 2 = Ended, 3 = Cancelled, 4 = Scheduled, 5 = Rejected, 6 = On Hold
 */
export enum AssignmentStatus {
  Pending = 0,
  Active = 1,
  Ended = 2,
  Cancelled = 3,
  Scheduled = 4,
  Rejected = 5,
  OnHold = 6,
}

/**
 * Pay Type Enum
 * 0 = None, 1 = Hourly, 2 = Salary
 */
export enum PayType {
  None = 0,
  Hourly = 1,
  Salary = 2,
}

/**
 * Overtime Status Enum
 * NonExempt or Exempt
 */
export enum OvertimeStatus {
  NonExempt = 'NonExempt',
  Exempt = 'Exempt',
}

/**
 * Change Request Type
 * 0 = PayUpdate, 1 = Termination
 */
export enum ChangeType {
  PayUpdate = 0,
  Termination = 1,
}

/**
 * Change Request Status
 * 0 = Pending, 1 = Approved, 2 = Rejected
 */
export enum ChangeRequestStatus {
  Pending = 0,
  Approved = 1,
  Rejected = 2,
}

/**
 * Assignment Response with populated fields
 */
export type CompensationHistoryStatus =
  | 'Hired'
  | 'Pay Update - Pending'
  | 'Pay Update'
  | 'Termination - Pending'
  | 'Terminated'
  | 'Canceled Request'
  | 'Pending' // Legacy status
  | 'Fulfilled' // Legacy status

export interface JobCompensationHistoryEntry {
  _id?: string // MongoDB ID for the history entry
  changeType?: 'compensation' | 'job' // Type of change (compensation or job/termination)
  effectiveDate: string
  payrate: string
  payType: number
  overTimeStatus: string
  comment: string
  createdBy?: string
  createdAt: string
  status: CompensationHistoryStatus
  overTimeRate?: string
  requestedBy?: string // Name of user who created this request
}

export interface AssignmentResponse {
  id: string
  assignmentId: string
  applicantId: string
  jobOrderId: string
  serviceOrderNumber: string
  serviceOrderId: string
  // STFFLG: delivery date from the associated service order (orders[0].distributionStartDate)
  deliveryDate?: Date | string
  employeeId: string
  startDate: Date | string
  payType: number
  assignmentStartDate: Date | string
  siteId: string
  departmentId: string
  positionId: string
  payrate: string
  hourlyBasedRate?: string
  billRate: string
  endDate?: Date | string
  overTimeStatus: number | string
  overtimeRate?: string
  status?: AssignmentStatus | string
  comment?: string
  jobTitle: string
  createdDate?: Date | string
  modifiedDate?: Date | string
  createdBy?: string
  modifiedBy?: string
  versionKey?: number
  companyId?: string
  // Job change history (new field name from backend)
  jobChangeHistory?: JobCompensationHistoryEntry[]
  // Legacy field name for backwards compatibility
  jobCompensationHistory?: JobCompensationHistoryEntry[]
  // Role on the job order — primary or backup (populated during fill/submit)
  role?: 'primary' | 'backup'
  // KRAF-730: remote worker — payroll uses the employee's HOME address as the
  // tax work location instead of the site address (multi-state tax routing)
  isRemote?: boolean
  // Populated fields
  companyName?: string
  siteName?: string
  departmentName?: string
  positionName?: string
  employeeName?: string
  applicantName?: string
  // Candidate-centric columns for the Assignments tab (last-4 SSN only — never the full SSN)
  firstName?: string
  lastName?: string
  last4SSN?: string
  phone?: string
  // Ronos (Staffology) sync status for this assignment
  ronosSync?: RonosSyncInfo
}

/**
 * Assignment Paging Response
 */
export interface AssignmentPagingResponse {
  assignments: AssignmentResponse[]
  pageNumber: number
  pageSize: number
  totalRecords: number
  totalPages: number
}

/**
 * Update Job Compensation Request
 * POST /api/assignment/{id}
 */
export interface UpdateJobCompensationRequest {
  effectiveDate: string // ISO date string
  payrate: string
  payType: PayType
  overTimeStatus: OvertimeStatus
  overTimeRate?: string // Required if overTimeStatus is NonExempt
  comment?: string
}

/**
 * Update Assignment End Date Request
 * PUT /api/assignment/updateAssignmentEndDate/{id}
 */
export interface UpdateAssignmentEndDateRequest {
  endDate: string | null // ISO date string or null to remove end date
  comment?: string
}

/**
 * Cancel Job Update Request
 * POST /api/assignment/cancelJobUpdate/{id}
 */
export interface CancelJobUpdateRequest {
  changeType: 'compensation' | 'job' // Type of change to cancel
  comment?: string
}

/**
 * Assignment API Service
 * Handles all assignment-related API calls
 */
export class AssignmentApi extends BaseApi {
  constructor() {
    super(import.meta.env.VITE_BACKEND_URL ?? 'http://localhost:5000')
  }

  /**
   * Get assignments with pagination and filtering
   * POST /api/assignment/assignments
   *
   * @param params - Paging and filter parameters
   */
  async getAssignments(params: AssignmentPagingParameters): Promise<AssignmentPagingResponse> {
    try {
      const response = await this.client.post<AssignmentPagingResponse>(
        '/api/assignment/assignments',
        params,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching assignments:', error)
      throw error
    }
  }

  /**
   * Update assignment status (Active/Inactive)
   * Similar to updateCompanyStatus pattern
   * PUT /api/assignment/updateAssignmentStatus/:id/Status/:status
   *
   * @param assignmentId - The assignment ID
   * @param status - The new status ('active' or 'inactive')
   * @param date - The activation/end date
   * @param note - Optional note
   */
  async updateAssignmentStatus(
    assignmentId: string,
    status: 'active' | 'inactive',
    date: string,
    note?: string,
  ): Promise<Assignment> {
    try {
      const response = await this.client.put<Assignment>(
        `/api/assignment/updateAssignmentStatus/${assignmentId}/Status/${status}`,
        {
          date,
          note,
        },
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error updating assignment status: ${assignmentId}`, error)
      throw error
    }
  }

  /**
   * Activate an assignment
   * PUT /api/assignment/updateAssignmentStatus/:id/Status/active
   *
   * @param assignmentId - The assignment ID
   * @param activeDate - The date the assignment becomes active
   * @param note - Optional note
   */
  async activateAssignment(
    assignmentId: string,
    activeDate: string,
    note?: string,
  ): Promise<Assignment> {
    try {
      const response = await this.client.put<Assignment>(
        `/api/assignment/updateAssignmentStatus/${assignmentId}/Status/active`,
        {
          date: activeDate,
          note,
        },
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error activating assignment: ${assignmentId}`, error)
      throw error
    }
  }

  /**
   * Update assignment end date (create termination request or reactivate)
   * PUT /api/assignment/updateAssignmentEndDate/{id}
   *
   * If endDate is future: Creates "Termination - Pending" record
   * If endDate is today/past: Creates "Terminated" record and sets assignment to Inactive
   * If endDate is null: Removes end date and reactivates assignment
   *
   * @param assignmentId - The assignment ID (MongoDB ID)
   * @param payload - End date payload with endDate (ISO string or null) and optional comment
   */
  async updateAssignmentEndDate(
    assignmentId: string,
    payload: UpdateAssignmentEndDateRequest,
  ): Promise<AssignmentResponse> {
    try {
      const response = await this.client.put<AssignmentResponse>(
        `/api/assignment/updateAssignmentEndDate/${assignmentId}`,
        payload,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error updating assignment end date: ${assignmentId}`, error)
      throw error
    }
  }

  /**
   * Update the remote-worker status of an assignment (KRAF-730)
   * PUT /api/assignment/updateRemoteStatus/{id}
   *
   * When isRemote is true, payroll uses the employee's HOME address as the
   * tax work location instead of the site address (multi-state tax routing).
   *
   * @param id - The assignment ID (MongoDB ID)
   * @param isRemote - Whether the assignment is a remote-worker assignment
   */
  async updateAssignmentRemoteStatus(id: string, isRemote: boolean): Promise<AssignmentResponse> {
    try {
      const response = await this.client.put<AssignmentResponse>(
        `/api/assignment/updateRemoteStatus/${id}`,
        {isRemote},
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error updating assignment remote status: ${id}`, error)
      throw error
    }
  }

  /**
   * Legacy method - kept for backwards compatibility
   * @deprecated Use updateAssignmentEndDate instead
   */
  async endAssignment(
    assignmentId: string,
    payload: UpdateAssignmentEndDateRequest,
  ): Promise<AssignmentResponse> {
    return this.updateAssignmentEndDate(assignmentId, payload)
  }

  /**
   * Update job compensation (create pay update request or immediate update based on effective date)
   * POST /api/assignment/updateJobCompensation/{id}
   *
   * If there's a pending compensation change, it updates that entry.
   * Otherwise, creates a new compensation entry at the beginning of history.
   * If effectiveDate is today/past: Status = "Fulfilled", assignment immediately updated
   * If effectiveDate is future: Status = "Pending"
   *
   * @param assignmentId - The MongoDB assignment ID
   * @param payload - Job compensation details
   */
  async updateJobCompensation(
    assignmentId: string,
    payload: UpdateJobCompensationRequest,
  ): Promise<AssignmentResponse> {
    try {
      const response = await this.client.post<AssignmentResponse>(
        `/api/assignment/updateJobCompensation/${assignmentId}`,
        payload,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error updating job compensation: ${assignmentId}`, error)
      throw error
    }
  }

  /**
   * Update assignment details
   * PUT /api/assignment
   *
   * @param data - Assignment data with all required fields
   */
  async updateAssignment(data: Record<string, unknown>): Promise<Assignment> {
    try {
      const response = await this.client.put<Assignment>(`/api/assignment`, data)
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error updating assignment: ${data._id || data.id}`, error)
      throw error
    }
  }

  /**
   * Cancel a pending job update (compensation or termination)
   * POST /api/assignment/cancelJobUpdate/{id}
   *
   * Cancels a pending job update by setting its status to "Canceled Request".
   * Only pending changes can be canceled.
   *
   * @param assignmentId - The assignment ID (MongoDB ID)
   * @param payload - Cancel job update payload with changeType ('compensation' or 'job') and optional comment
   */
  async cancelJobUpdate(
    assignmentId: string,
    payload: CancelJobUpdateRequest,
  ): Promise<AssignmentResponse> {
    try {
      const response = await this.client.put<AssignmentResponse>(
        `/api/assignment/cancelJobUpdate/${assignmentId}`,
        payload,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error canceling job update: ${assignmentId}`, error)
      throw error
    }
  }

  /**
   * Legacy method - kept for backwards compatibility
   * @deprecated Use cancelJobUpdate instead
   */
  async cancelChangeRequest(
    assignmentId: string,
    changeType: 'compensation' | 'job',
  ): Promise<AssignmentResponse> {
    return this.cancelJobUpdate(assignmentId, {changeType})
  }

  /**
   * Get a single assignment by ID
   * GET /api/assignment/:id
   *
   * @param assignmentId - The assignment ID
   */
  async getAssignmentById(assignmentId: string): Promise<Assignment> {
    try {
      const response = await this.client.get<Assignment>(`/api/assignment/${assignmentId}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error fetching assignment: ${assignmentId}`, error)
      throw error
    }
  }

  /**
   * Get assignments by applicant ID
   * GET /api/assignment/by-applicant/:applicantId
   *
   * @param applicantId - The applicant ID
   */
  async getAssignmentsByApplicant(applicantId: string): Promise<AssignmentResponse[]> {
    try {
      const response = await this.client.get<AssignmentResponse[]>(
        `/api/assignment/by-applicant/${applicantId}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error fetching assignments by applicant: ${applicantId}`, error)
      throw error
    }
  }

  /**
   * Get a single assignment by assignment ID with populated fields
   * GET /api/assignment/by-assignment-id/:assignmentId
   *
   * @param assignmentId - The assignment ID
   */
  async getAssignmentByAssignmentId(assignmentId: string): Promise<AssignmentResponse> {
    try {
      const response = await this.client.get<AssignmentResponse>(
        `/api/assignment/by-assignment-id/${assignmentId}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error fetching assignment by assignment ID: ${assignmentId}`, error)
      throw error
    }
  }

  /**
   * Manually trigger a Ronos (Staffology) sync for a single assignment.
   * If the employee was never created in Ronos it will create them, otherwise
   * it updates the existing assignment.
   * POST /api/assignment/sync-ronos/by-assignment/:assignmentId
   *
   * @param assignmentId - The business assignment ID (e.g. EA000001)
   */
  async syncAssignmentWithRonos(assignmentId: string): Promise<RonosSyncResult> {
    try {
      const response = await this.client.post<RonosSyncResult>(
        `/api/assignment/sync-ronos/by-assignment/${assignmentId}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error syncing assignment with Ronos: ${assignmentId}`, error)
      throw error
    }
  }

  /**
   * Get a single assignment by assignment ID with populated fields
   * GET /api/assignment/by-assignment-id/:assignmentId
   *
   * @param assignmentId - The assignment ID
   */
  async getAssignmentByAssignmentIdSpecifically(assignmentId: string): Promise<AssignmentResponse> {
    try {
      const response = await this.client.get<AssignmentResponse>(`/api/assignment/${assignmentId}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error fetching assignment by assignment ID: ${assignmentId}`, error)
      throw error
    }
  }

  /**
   * Update direct deposit information for an assignment
   * PUT /api/assignment/:id/directDeposit
   * Based on SHR-FE updateEmployeeDirectDeposit pattern
   *
   * @param assignmentId - The assignment ID
   * @param directDeposit - Array of direct deposit details
   */
  async updateAssignmentDirectDeposit(
    assignmentId: string,
    directDeposit: Array<{
      bankName: string
      bankAccountType: 'Checking' | 'Savings'
      accountNumber: string
      routingNumber: string
      percentage: string
    }>,
  ): Promise<Assignment> {
    try {
      const response = await this.client.put<Assignment>(
        `/api/assignment/${assignmentId}/directDeposit`,
        {directDeposit},
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error updating assignment direct deposit: ${assignmentId}`, error)
      throw error
    }
  }

  // ========== NEW ASSIGNMENT SCHEDULING ENDPOINTS ==========

  /**
   * Schedule assignment activation
   * POST /api/assignment/scheduleActivation/:id
   *
   * @param assignmentId - The assignment ID
   * @param scheduledDate - The date to activate the assignment
   */
  async scheduleAssignmentActivation(assignmentId: string, scheduledDate: string): Promise<void> {
    try {
      await this.client.post(`/api/assignment/scheduleActivation/${assignmentId}`, {
        scheduledDate,
      })
    } catch (error: unknown) {
      reportCatchError(`Error scheduling assignment activation: ${assignmentId}`, error)
      throw error
    }
  }

  /**
   * Schedule assignment end
   * POST /api/assignment/scheduleEnd/:id
   *
   * @param assignmentId - The assignment ID
   * @param scheduledDate - The date to end the assignment
   */
  async scheduleAssignmentEnd(assignmentId: string, scheduledDate: string): Promise<void> {
    try {
      await this.client.post(`/api/assignment/scheduleEnd/${assignmentId}`, {
        scheduledDate,
      })
    } catch (error: unknown) {
      reportCatchError(`Error scheduling assignment end: ${assignmentId}`, error)
      throw error
    }
  }

  /**
   * Cancel scheduled activation
   * DELETE /api/assignment/cancelScheduledActivation/:id
   *
   * @param assignmentId - The assignment ID
   */
  async cancelScheduledActivation(assignmentId: string): Promise<void> {
    try {
      await this.client.delete(`/api/assignment/cancelScheduledActivation/${assignmentId}`)
    } catch (error: unknown) {
      reportCatchError(`Error canceling scheduled activation: ${assignmentId}`, error)
      throw error
    }
  }

  /**
   * Cancel scheduled end
   * DELETE /api/assignment/cancelScheduledEnd/:id
   *
   * @param assignmentId - The assignment ID
   */
  async cancelScheduledEnd(assignmentId: string): Promise<void> {
    try {
      await this.client.delete(`/api/assignment/cancelScheduledEnd/${assignmentId}`)
    } catch (error: unknown) {
      reportCatchError(`Error canceling scheduled end: ${assignmentId}`, error)
      throw error
    }
  }

  /**
   * Update assignment end date by human-readable Assignment ID (e.g., "EA000039")
   * PUT /api/assignment/updateAssignmentEndDateByAssignmentId/{assignmentId}
   *
   * Updates the end date for an assignment using the assignmentId field (e.g., "EA000039").
   * This creates a job change record in the history.
   * If setting a future end date, creates a "Termination – Pending" record.
   * If the end date is today or past, creates a "Terminated" record and sets assignment to Inactive.
   *
   * @param assignmentId - The human-readable assignment ID (e.g., "EA000039")
   * @param payload - End date payload with endDate (ISO string) and optional comment
   */
  async updateAssignmentEndDateByAssignmentId(
    assignmentId: string,
    payload: {endDate: string; comment?: string},
  ): Promise<AssignmentResponse> {
    try {
      const response = await this.client.put<AssignmentResponse>(
        `/api/assignment/updateAssignmentEndDateByAssignmentId/${assignmentId}`,
        payload,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError(
        `Error updating assignment end date by assignment ID: ${assignmentId}`,
        error,
      )
      throw error
    }
  }

  /**
   * Get job titles for a company
   * GET /api/assignment/job-titles
   *
   * Returns an array of job title strings. Use in dropdowns/selects.
   * The response is just the title string (no ID), pass the selected string as-is.
   *
   * @param companyId - Company ID (required)
   * @param siteId - Site ID (optional) - filters job titles by site
   * @param departmentId - Department ID (optional) - filters job titles by department
   */
  async getJobTitles(companyId: string, siteId?: string, departmentId?: string): Promise<string[]> {
    try {
      const params = new URLSearchParams({companyId})
      if (siteId) params.append('siteId', siteId)
      if (departmentId) params.append('departmentId', departmentId)

      const response = await this.client.get<string[]>(
        `/api/assignment/job-titles?${params.toString()}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error fetching job titles: ${companyId}`, error)
      throw error
    }
  }
}

// Export a singleton instance
export const assignmentApi = new AssignmentApi()

export default assignmentApi
